// The shape of a member id, and the single place it is written down.
//
// Galapagos mints every NEW member id as an RFC 4122 v4 UUID, but a migrated
// member keeps backend2's `users.id` as its `members.id` and Cognito Username,
// and backend2 never constrained that column beyond the 36-character 8-4-4-4-12
// hex GUID shape: a confirmed signup writes the Cognito sub into it, other paths
// generate GUIDs whose version and variant nibbles are not RFC 4122's.
// Validating such an id as v4 rejects real members — and, in the mirror-image
// case, a guard that only recognises v4 fails to notice a real member id
// leaking somewhere ids do not belong.
//
// This is the lowest package in the `shared -> observability -> product-analytics`
// dependency chain, which is why the grammar lives here rather than beside the
// other identity helpers: `@galapagos/shared` already depends on this package,
// so the reverse import would close a cycle.
// `packages/shared/src/identity/legacy-identity-id.ts` re-exports everything
// below under its established names.
//
// The hex class is spelled out rather than using the `i` flag so the same source
// renders as a flag-free OpenAPI `pattern`. Both regexes are built from one body
// so the anchored and unanchored forms cannot drift.

import { z } from 'zod';

const LEGACY_IDENTITY_ID_BODY =
  '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/** The whole value is an 8-4-4-4-12 hex GUID. */
export const LEGACY_IDENTITY_ID_PATTERN = new RegExp(`^${LEGACY_IDENTITY_ID_BODY}$`);

/**
 * An 8-4-4-4-12 hex GUID appears somewhere inside a longer string — for guards
 * that detect an id embedded in a value it has no business being part of.
 */
export const LEGACY_IDENTITY_ID_SUBSTRING_PATTERN = new RegExp(LEGACY_IDENTITY_ID_BODY);

export function isLegacyIdentityId(value: string): boolean {
  return LEGACY_IDENTITY_ID_PATTERN.test(value);
}

/** backend2 `users.id` as the estate actually issues it. */
export const legacyIdentityIdSchema = z
  .string()
  .regex(LEGACY_IDENTITY_ID_PATTERN, 'must be an 8-4-4-4-12 hex GUID');

/**
 * A `members.id` on the wire: a minted v4 UUID or an imported legacy GUID. Both
 * satisfy this shape; nothing narrower is valid for a column that holds both.
 */
export const memberIdSchema = legacyIdentityIdSchema;
